// src/features/patient/care-treatment/utils/useCareTreatmentData.ts

/**
 * Purpose : The one place the Care & Treatment screens get visits
 * and notes from: GET /api/patient/care-timeline for the profile being viewed
 * (the patient or a dependent).
 *
 * Notes come newest first, one page at a time; "Load older" fetches the next
 * page (X-Next-Before) and keeps it under the live first page. A header search
 * result for an older note fetches the page starting at that note's day
 * (loadNoteAt). The summary numbers come from the server (counts), so they
 * never depend on what's loaded. Visit answers given this session apply to
 * older pages too; failures show a toast; a failed first load reports isError.
 *
 * Contents :
 *
 * * function(useCareTreatmentData) : visits and notes for the acting profile
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getOlderCareNotes } from "@/features/patient/care-treatment/api/careTimelineApi";
import type { CareTimelineData, Note } from "@/features/patient/care-treatment/api/careTimelineTypes";
import { careTimelineQuery, useNoteAnswers, type NoteAnswers } from "@/portals/patient/api/patientQueries";
import { toastError } from "@/shared/lib/toast";
import { useFamily } from "@/portals/patient/context/FamilyContext";
import { pollingEndsAt, pollingGaveUp } from "@/features/patient/care-treatment/utils/careTimelinePolling";

interface OlderPages {
  key: string;
  notes: Note[];
  /** The cursor for the next older page; undefined until one was loaded. */
  nextBefore?: string | null;
}

/** The day after a YYYY-MM-DD day: the "before" cursor whose page starts on that day. */
function dayAfter(day: string): string {
  const d = new Date(`${day.slice(0, 10)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/** An older-page note as the patient answered it this session (the first page is updated in the cache). */
function withAnswer(note: Note, answers: NoteAnswers): Note {
  const answer = answers[note.id];
  if (!answer || note.encounterId !== null) return note;
  if (typeof answer === "object") {
    return { ...note, encounterId: answer.visit, linkConfidence: "high", suggestedEncounterId: undefined };
  }
  if (answer === "confirm") {
    return note.suggestedEncounterId
      ? { ...note, encounterId: note.suggestedEncounterId, linkConfidence: "high", suggestedEncounterId: undefined }
      : note;
  }
  return { ...note, linkConfidence: "low", suggestedEncounterId: undefined, keptUnlinked: true };
}

// function(useCareTreatmentData)
/**
 * Visits and notes for the profile being viewed.
 *
 * @returns Encounters, notes (first page plus any older pages loaded),
 *   documents, server counts, loading and error state, retry, "load older"
 *   and whether the background checks for scanned pages' text have stopped.
 */
export function useCareTreatmentData(): CareTimelineData & { loadNoteAt: (day: string) => Promise<Note[] | null> } {
  const { activeDependent } = useFamily();
  const key = activeDependent?.id ?? "";
  const { data, isLoading, isError, refetch, dataUpdatedAt } = useQuery(careTimelineQuery(activeDependent?.id));
  const answers = useNoteAnswers(activeDependent?.id);
  // Re-render once when the checks stop, so the "check back later" note appears.
  const [, setTick] = useState(0);
  useEffect(() => {
    const endsAt = pollingEndsAt(key);
    if (endsAt === undefined) return undefined;
    const timer = window.setTimeout(() => setTick((t) => t + 1), Math.max(0, endsAt - Date.now()) + 50);
    return () => window.clearTimeout(timer);
  }, [key, dataUpdatedAt]);

  const [older, setOlder] = useState<OlderPages>({ key, notes: [] });
  const [loadingOlder, setLoadingOlder] = useState(false);
  // Another profile: its own pages.
  const pages = older.key === key ? older : { key, notes: [] };
  const cursor = pages.nextBefore === undefined ? data?.nextBefore ?? null : pages.nextBefore;

  const loadOlder = useCallback(() => {
    if (!cursor || loadingOlder) return;
    setLoadingOlder(true);
    getOlderCareNotes(cursor, activeDependent?.id)
      .then((page) => setOlder((current) => ({
        key,
        notes: [...(current.key === key ? current.notes : []), ...page.notes],
        nextBefore: page.nextBefore,
      })))
      .catch(() => toastError("Couldn't load older notes. Please try again."))
      .finally(() => setLoadingOlder(false));
  }, [cursor, loadingOlder, activeDependent?.id, key]);

  // Pages fetched for a search result's day, kept apart from "Load older"'s run of pages.
  const [jumped, setJumped] = useState<{ key: string; days: string[]; notes: Note[] }>({ key, days: [], notes: [] });
  const jumps = jumped.key === key ? jumped : { key, days: [], notes: [] };
  // Resolves with that page's notes ([] when there was nothing to fetch), or null when it failed.
  const loadNoteAt = useCallback((day: string): Promise<Note[] | null> => {
    if (!day) return Promise.resolve([]);
    if (jumps.days.includes(day)) return Promise.resolve(jumps.notes);
    setJumped({ ...jumps, days: [...jumps.days, day] });
    return getOlderCareNotes(dayAfter(day), activeDependent?.id)
      .then((page) => {
        setJumped((current) => (current.key === key ? { ...current, notes: [...current.notes, ...page.notes] } : current));
        return page.notes;
      })
      .catch(() => {
        // Forget the day so a later try fetches it again.
        setJumped((current) => (current.key === key ? { ...current, days: current.days.filter((d) => d !== day) } : current));
        toastError("Couldn't open that note. Please try again.");
        return null;
      });
  }, [jumps, activeDependent?.id, key]);

  // The live first page wins over an older copy of the same note.
  const notes = useMemo(() => {
    const first = data?.notes ?? [];
    const seen = new Set(first.map((n) => n.id));
    const out = [...first];
    for (const n of [...pages.notes, ...jumps.notes]) {
      if (!seen.has(n.id)) {
        seen.add(n.id);
        out.push(withAnswer(n, answers));
      }
    }
    return out;
  }, [data?.notes, pages.notes, jumps.notes, answers]);

  return {
    encounters: data?.encounters ?? [],
    notes,
    documents: data?.documents ?? [],
    isLoading,
    // Only when there is nothing to show: a failed background re-check keeps the last good copy on screen.
    isError: isError && !data,
    retry: () => void refetch(),
    stillProcessing: pollingGaveUp(key, data),
    counts: data?.counts,
    identityPending: data?.identityPending ?? null,
    uploadSummary: data?.uploadSummary ?? null,
    careTeam: data?.careTeam,
    carePlans: data?.carePlans,
    hasOlder: Boolean(cursor),
    loadingOlder,
    loadOlder,
    loadNoteAt,
  };
}
